import { forwardRef, useImperativeHandle, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import TabErrorBanner from '@/components/v2/molecules/admin/shared/TabErrorBanner';
import PropertiesFilters from '@/components/v2/molecules/admin/tabs/AdminPropertiesTab/PropertiesFilters';
import PropertiesTable from '@/components/v2/molecules/admin/tabs/AdminPropertiesTab/PropertiesTable';
import TablePagination from '@/components/v2/molecules/admin/shared/TablePagination';
import PropertyRulesModal from '@/components/v2/organisms/admin/tabs/AdminPropertiesTab/PropertyRulesModal';
import PropertyWifiModal from '@/components/v2/organisms/admin/tabs/AdminPropertiesTab/PropertyWifiModal';
import PropertyEntryModal from '@/components/v2/organisms/admin/tabs/AdminPropertiesTab/PropertyEntryModal';
import { usePropertiesFiltered, PropertyResult } from '@/hooks/admin/tabs/usePropertiesFiltered';
import { Property } from '@/types/property';
import useRolePermissions from '@/hooks/admin/shared/useRolePermissions';
import { useOrganizations } from '@/hooks/useOrganizations';

export interface PropertiesListViewRef {
  createNew: () => void;
}

// Helper to map PropertyResult to Property type for modals/table
const mapResultToProperty = (result: PropertyResult): Property => ({
  id: result.id,
  title: result.title,
  location: result.location,
  shortDescription: result.short_description,
  longDescription: result.long_description,
  price: result.price,
  cleaningFee: result.cleaning_fee,
  operationStartedAt: result.operation_started_at || '',
  rating: result.rating,
  guests: result.guests,
  images: result.images,
  bedrooms: result.bedrooms,
  bathrooms: result.bathrooms,
  area: result.area || 0,
  available: result.available,
  latitude: result.latitude || undefined,
  longitude: result.longitude || undefined,
  shortName: result.short_name || undefined,
  name: result.name || undefined,
  featured: result.featured,
  airbnbUrl: result.airbnb_url || undefined,
  airbnbIcal: result.airbnb_ical || undefined,
  propertyFloor: result.property_floor || undefined,
  hostId: result.host_id || undefined,
  neighborhoodId: result.neighborhood_id || undefined,
  wifiNetwork: result.wifi_network || undefined,
  wifiPassword: result.wifi_password || undefined,
  entryDescription: result.entry_description || undefined,
  entryVideoUrl: result.entry_video_url || undefined,
  securityCode: result.security_code || undefined,
  sortOrder: result.sort_order,
  neighborhood: result.neighborhood_city ? {
    city: result.neighborhood_city,
    state: result.neighborhood_state || '',
    country: result.neighborhood_country || '',
    description: '',
    longDescription: '',
  } : undefined,
  host: result.host_name ? {
    name: result.host_name,
    avatar: result.host_avatar || '',
    superHost: false,
    experience: '',
  } : undefined,
});

const PropertiesListView = forwardRef<PropertiesListViewRef>((_, ref) => {
  const { canDelete } = useRolePermissions();
  const navigate = useNavigate();

  const { data: organizations = [] } = useOrganizations();

  const {
    properties,
    totalCount,
    totalPages,
    isLoading: isLoadingProperties,
    error: errorProperties,
    refetch: refetchProperties,
    currentPage,
    pageSize,
    handlePageChange,
    handlePageSizeChange,
    searchTerm,
    selectedOrganizations,
    availabilityFilter,
    hasActiveFilters,
    handleSearchTermChange,
    handleOrganizationsChange,
    handleAvailabilityChange,
    resetFilters,
  } = usePropertiesFiltered();

  const [rulesProperty, setRulesProperty] = useState<Property | null>(null);
  const [wifiProperty, setWifiProperty] = useState<Property | null>(null);
  const [entryProperty, setEntryProperty] = useState<Property | null>(null);

  useImperativeHandle(ref, () => ({
    createNew: () => navigate('/admin/properties/create'),
  }));

  // Map results to Property type
  const mappedProperties = properties.map(mapResultToProperty);

  const handleEdit = (property: Property) => {
    navigate(`/admin/properties/edit/${property.id}`);
  };

  if (errorProperties) {
    return (
      <TabErrorBanner
        title="Error al cargar propiedades"
        message="No se pudieron cargar las propiedades. Por favor, intenta nuevamente."
        onRetry={() => refetchProperties()}
      />
    );
  }

  return (
    <div className="space-y-6">
      <PropertiesFilters
        searchTerm={searchTerm}
        selectedOrganizations={selectedOrganizations}
        availabilityFilter={availabilityFilter}
        onSearchChange={handleSearchTermChange}
        onOrganizationsChange={handleOrganizationsChange}
        onAvailabilityChange={handleAvailabilityChange}
        organizations={organizations}
        hasActiveFilters={hasActiveFilters}
        onClearFilters={resetFilters}
      />

      {isLoadingProperties ? (
        <div className="space-y-4 border rounded-lg p-6">
          {Array.from({ length: 5 }).map((_, index) => (
            <div
              key={index}
              className="bg-muted rounded h-12 animate-pulse"
            />
          ))}
        </div>
      ) : mappedProperties.length === 0 && hasActiveFilters ? (
        <div className="text-center py-12 border rounded-lg">
          <p className="text-muted-foreground mb-4">
            No se encontraron propiedades con los filtros aplicados
          </p>
          <Button
            variant="outline"
            onClick={resetFilters}
          >
            Limpiar búsqueda
          </Button>
        </div>
      ) : (
        <>
          <PropertiesTable
            properties={mappedProperties}
            onEdit={handleEdit}
            onOpenRules={(property) => setRulesProperty(property)}
            onOpenEntry={(property) => setEntryProperty(property)}
            onOpenWifi={(property) => setWifiProperty(property)}
            canDelete={canDelete}
          />
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalCount={totalCount}
            pageSize={pageSize}
            onPageChange={handlePageChange}
            onPageSizeChange={handlePageSizeChange}
          />
        </>
      )}

      <PropertyRulesModal
        isOpen={!!rulesProperty}
        onClose={() => setRulesProperty(null)}
        property={rulesProperty}
      />

      <PropertyWifiModal
        isOpen={!!wifiProperty}
        onClose={() => setWifiProperty(null)}
        property={wifiProperty}
      />

      <PropertyEntryModal
        isOpen={!!entryProperty}
        onClose={() => setEntryProperty(null)}
        property={entryProperty}
      />
    </div>
  );
});

PropertiesListView.displayName = 'PropertiesListView';

export default PropertiesListView;
